import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, FileText, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentDistributionPanel } from '@/components/workflows/DocumentDistributionPanel';

interface ConductMeetingPhase4Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

/**
 * Phase 4: Distribute & Archive
 * Distribute finalized minutes to members, create action item tasks, complete workflow
 */
export function ConductMeetingPhase4({ taskId, unaId, task, phaseData, onPhaseComplete }: ConductMeetingPhase4Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [distributionRecipients, setDistributionRecipients] = useState<any[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [meetingMinutesId, setMeetingMinutesId] = useState<string | null>(null);
  const [hasUnaMembers, setHasUnaMembers] = useState(false);

  const workflowData = task?.workflow_data || {};

  useEffect(() => {
    loadMeetingAndDocuments();
    extractDistributionList();
    checkUnaMembers();
    // Check if task is already completed
    if (task?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [taskId, task?.status]);

  async function checkUnaMembers() {
    try {
      const { count, error } = await supabase
        .from('una_members')
        .select('id', { count: 'exact', head: true })
        .eq('una_id', unaId)
        .eq('status', 'active');

      if (error) throw error;
      setHasUnaMembers((count ?? 0) > 0);
    } catch (error) {
      console.error('Error checking UNA members:', error);
      setHasUnaMembers(false);
    }
  }

  useEffect(() => {
    checkCompletionStatus();
  }, [documents]);

  function extractDistributionList() {
    const distributionList = workflowData.distribution_list || [];
    
    if (!Array.isArray(distributionList)) {
      console.warn('distribution_list is not an array:', distributionList);
      setDistributionRecipients([]);
      return;
    }
    
    const recipients = distributionList.map((item: any) => ({
      id: `external_${item.email}`,
      name: item.name,
      email: item.email,
      type: 'external'
    }));
    
    setDistributionRecipients(recipients);
  }

  async function loadMeetingAndDocuments() {
    try {
      setLoading(true);
      
      // Get meeting_minutes record
      const { data: minutes, error: minutesError } = await supabase
        .from('meeting_minutes')
        .select('id')
        .eq('task_id', taskId)
        .maybeSingle();

      if (!minutesError && minutes) {
        setMeetingMinutesId(minutes.id);
      }
      
      // Load finalized documents for this task (formal_meeting_minutes)
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', taskId)
        .in('status', ['finalized', 'final'])
        .order('generated_at', { ascending: true });

      if (error) throw error;
      
      setDocuments(data || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function checkCompletionStatus() {
    await checkDistributionComplete();
  }

  async function checkDistributionComplete(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('document_distribution_log')
        .select('id, status')
        .eq('task_id', taskId);

      if (error) throw error;

      const isComplete = data && data.length > 0 && data.every(d => d.status === 'sent');
      setDistributionComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error('Error checking distribution status:', error);
      return false;
    }
  }

  async function handleDistributionComplete() {
    await checkDistributionComplete();
  }

  async function createTasksFromActionItems() {
    try {
      // Get meeting_minutes to access action_items
      const { data: minutes, error: minutesError } = await supabase
        .from('meeting_minutes')
        .select('action_items')
        .eq('task_id', taskId)
        .single();

      if (minutesError || !minutes?.action_items) {
        console.log('No action items to create tasks for');
        return;
      }

      // Type cast action_items as array
      const actionItems = Array.isArray(minutes.action_items) ? minutes.action_items : [];
      if (actionItems.length === 0) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Create tasks for each action item
      for (const item of actionItems) {
        const actionItem = item as Record<string, any>;
        const taskData = {
          task_title: actionItem.description || actionItem.action || 'Action Item from Meeting',
          task_description: actionItem.notes || '',
          due_date: actionItem.due_date || null,
          urgency: actionItem.priority === 'high' ? 'high' : actionItem.priority === 'low' ? 'low' : 'medium',
          importance: actionItem.priority === 'high' ? 'high' : 'medium',
          status: 'not_started',
          una_id: unaId,
          user_id: user.id,
          category_type: 'meeting',
          source_meeting_date: workflowData.meeting_date || null,
        };

        const { error: taskError } = await supabase
          .from('custom_tasks')
          .insert(taskData);

        if (taskError) {
          console.error('Error creating task from action item:', taskError);
        }
      }

      console.log(`Created ${actionItems.length} tasks from action items`);
    } catch (error) {
      console.error('Error creating tasks from action items:', error);
    }
  }

  async function handleMarkComplete() {
    try {
      setIsCompleting(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create tasks from action items
      await createTasksFromActionItems();

      // Use security definer function to bypass RLS recursion
      const { data: success, error: taskError } = await supabase
        .rpc('complete_task', {
          p_task_id: taskId,
          p_user_id: user.id
        });

      if (taskError) throw taskError;
      if (!success) throw new Error('Failed to complete task');

      // Update meeting_minutes status to 'finalized'
      if (meetingMinutesId) {
        const { error: minutesError } = await supabase
          .from('meeting_minutes')
          .update({ status: 'finalized' })
          .eq('id', meetingMinutesId);

        if (minutesError) {
          console.error('Error updating meeting minutes status:', minutesError);
        }
      }

      setIsCompleted(true);
      toast({
        title: 'Workflow Complete',
        description: 'Meeting workflow completed successfully. Minutes distributed and archived.',
      });
      onPhaseComplete?.();
    } catch (error: any) {
      console.error('Error completing workflow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete workflow',
        variant: 'destructive',
      });
    } finally {
      setIsCompleting(false);
    }
  }

  // Check if there are any recipients to distribute to
  const hasAnyRecipients = hasUnaMembers || distributionRecipients.length > 0;
  
  // Distribution is optional - workflow can complete without sending documents
  const allComplete = true;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show completion state if already completed
  if (isCompleted) {
    return (
      <div className="space-y-6">
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-800 dark:text-green-400">Meeting Workflow Completed</h3>
                <p className="text-sm text-green-700 dark:text-green-500">
                  Minutes have been distributed and are available in Meeting Records.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 4: Distribute & Archive</CardTitle>
                <Badge>Final Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Distribute finalized minutes to stakeholders and complete the meeting workflow
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Distribute finalized minutes to UNA members and distribution list</li>
              <li>Action items will be created as tasks automatically</li>
              <li>Documents are automatically saved to Meeting Records</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phase 4 Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between p-2 border rounded">
            <span>Document Distribution</span>
            {!hasAnyRecipients ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">No Recipients</span>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
            ) : distributionComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <span className="text-sm text-muted-foreground">Pending</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2 border rounded bg-muted/30">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span>Meeting Records Filing</span>
            </div>
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-xs text-muted-foreground italic">
            Documents are automatically linked to Meeting Records via this workflow.
          </p>
        </CardContent>
      </Card>

      {/* Document Distribution - Only show if there are recipients */}
      {hasAnyRecipients ? (
        <DocumentDistributionPanel
          taskId={taskId}
          unaId={unaId}
          documents={documents}
          additionalRecipients={distributionRecipients}
          onDistributionComplete={handleDistributionComplete}
        />
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Users className="h-5 w-5" />
              <div>
                <p className="font-medium text-foreground">No Members to Distribute To</p>
                <p className="text-sm">
                  There are no UNA members or distribution list contacts configured. 
                  You can proceed to complete the workflow.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complete Phase - Always show, but disable when not ready */}
      <Alert className={!allComplete ? 'border-muted' : 'border-green-200 bg-green-50 dark:bg-green-950/20'}>
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Complete Meeting Workflow</p>
              <p className="text-sm text-muted-foreground">
                {distributionComplete 
                  ? 'Documents distributed. Ready to complete workflow.'
                  : 'You can optionally distribute documents before completing, or complete now.'
                }
              </p>
            </div>
            <Button 
              onClick={handleMarkComplete} 
              disabled={!allComplete || isCompleting}
            >
              {isCompleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completing...
                </>
              ) : (
                'Complete Workflow'
              )}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
